import React from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Steps } from '@/components/steps';
import { cn } from '@/lib/utils';
import useDraftsHandler from '../controllers/useDraftsHandler';

// Display-only copy, keyed by screen type.
const NOUNS = {
  engagement: 'engagement',
  cta: 'CTA',
  endscreen: 'end screen',
  recirculations: 'recirculation',
};

const LEADS = {
  engagement: 'A poll, quiz or slider poll that agents show to visitors.',
  cta: 'A call to action that turns visitors into leads, sign-ups or clicks.',
  endscreen: 'The last screen visitors see, with a clear next step.',
  recirculations: 'Content your agents recommend across your site.',
};

const TITLE_PREFIX = {
  create: 'New',
  agentCreate: 'New',
  edit: 'Edit',
  agentEdit: 'Edit',
  draft: 'Finish draft',
};

/**
 * Header of the screen create / edit pages (and the agent drawers that reuse them):
 * the title says what is being made, the stepper shows where you are, and "Save as
 * draft" sits on the right. Navigation back lives in the breadcrumb.
 */
function GlobalSubTopBar({
  data,
  currentStep,
  backRoute,
  steps,
  creationType,
  isAgentScreen = false,
}) {
  const { handleCreateDraft, isCreatingDraft } = useDraftsHandler({
    draftType: data?.draft?.value,
    navigateBack: backRoute,
  });

  const noun = NOUNS[data?.type] || data?.createLabel || data?.type;
  const prefix = TITLE_PREFIX[creationType];
  const showDraftButton = !isAgentScreen && Boolean(data?.draft);
  const showSteps = steps?.length > 1;
  const hasTypeStep = steps?.[0]?.title === 'Type';

  return (
    <div className="flex w-full flex-wrap items-center justify-between gap-x-8 gap-y-4">
      <div className="flex min-w-0 flex-1 basis-72 flex-col gap-1">
        <h1 className="text-2xl leading-tight font-bold tracking-tight text-balance text-foreground md:text-3xl">
          {prefix ? (
            <>
              {prefix} {noun}
            </>
          ) : (
            <Skeleton className="h-8 w-56" />
          )}
        </h1>
        {LEADS[data?.type] && (
          <p className="hidden text-sm text-pretty text-muted-foreground sm:block">
            {LEADS[data?.type]}
          </p>
        )}
      </div>

      {(showSteps || showDraftButton) && (
        <div className="flex w-full flex-wrap items-center gap-x-6 gap-y-3 lg:w-auto">
          {showSteps && (
            <p className="min-w-0 truncate text-sm text-muted-foreground tabular-nums sm:hidden">
              Step {Math.min(currentStep, steps.length - 1) + 1} of {steps.length}
              <span className="font-medium text-foreground">
                {' · '}
                {steps[Math.min(currentStep, steps.length - 1)]?.title}
              </span>
            </p>
          )}
          {showSteps && (
            <Steps
              current={currentStep}
              size="sm"
              items={steps}
              responsive={false}
              className={cn(
                'hidden min-w-0 flex-1 sm:flex lg:flex-none',
                steps.length > 2 ? 'lg:w-[360px]' : 'lg:w-[240px]'
              )}
            />
          )}
          {showDraftButton && (
            <Button
              type="button"
              variant="outline"
              onClick={handleCreateDraft}
              disabled={isCreatingDraft || currentStep === 0}
              title={currentStep === 0 && hasTypeStep ? 'Pick a type first' : undefined}
              className="ms-auto"
            >
              {isCreatingDraft && <Spinner data-icon="inline-start" />}
              Save as draft
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default GlobalSubTopBar;
